export const formatMonth = (date: Date): string => {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = months[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2);
  return `${month}-${year}`;
};

export const getCurrentMonth = (): string => {
  return formatMonth(new Date());
};

export const getLastMonth = (): string => {
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return formatMonth(lastMonth);
};

export const getFinancialYearStartMonthIndex = (fyStartMonth: string): number => {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const fyStartMonthIndex = months.indexOf(fyStartMonth);
  if (fyStartMonthIndex === -1) {
    // Default to January if not found
    return 0;
  }
  return fyStartMonthIndex;
};

export const getFinancialYearStart = (referenceDate: Date, fyStartMonth?: string): string => {
  const fyStartMonthIndex = getFinancialYearStartMonthIndex(fyStartMonth ?? 'Jan');

  const fyStartDate =
    referenceDate.getMonth() >= fyStartMonthIndex
      ? new Date(referenceDate.getFullYear(), fyStartMonthIndex, 1)
      : new Date(referenceDate.getFullYear() - 1, fyStartMonthIndex, 1);

  return formatMonth(fyStartDate);
};

export const getCurrentFinancialYearStart = (fyStartMonth?: string): string => {
  return getFinancialYearStart(new Date(), fyStartMonth);
};

export const getFinancialYearEnd = (referenceDate: Date, fyStartMonth?: string): string => {
  const fyStartMonthIndex = getFinancialYearStartMonthIndex(fyStartMonth ?? 'Jan');

  // Financial year end is the month before the start month
  const fyEndMonthIndex = (fyStartMonthIndex - 1 + 12) % 12;

  // Determine which year the FY ends in
  const fyStartDate =
    referenceDate.getMonth() >= fyStartMonthIndex
      ? new Date(referenceDate.getFullYear(), fyStartMonthIndex, 1)
      : new Date(referenceDate.getFullYear() - 1, fyStartMonthIndex, 1);

  // If end month is before start month, FY ends in the next calendar year
  const fyEndYear =
    fyEndMonthIndex < fyStartMonthIndex ? fyStartDate.getFullYear() + 1 : fyStartDate.getFullYear();

  const fyEndDate = new Date(fyEndYear, fyEndMonthIndex, 1);

  return formatMonth(fyEndDate);
};

export const getReportingPeriodDisplay = (
  dashboardReportingPeriod: any,
  fy_start_month: string | undefined,
  preset: string = 'Custom',
) => {
  let dateRange = '';

  switch (preset) {
    case 'Custom':
      if (dashboardReportingPeriod?.start && dashboardReportingPeriod?.end) {
        dateRange = ` (${dashboardReportingPeriod.start} - ${dashboardReportingPeriod.end})`;
      }
      break;
    case 'Current Month': {
      const currentMonth = getCurrentMonth();
      dateRange = ` (${currentMonth})`;
      break;
    }
    case 'Last Month': {
      const lastMonth = getLastMonth();
      dateRange = ` (${lastMonth})`;
      break;
    }
    case 'Financial Year to Last Month': {
      const fyStartMonth = fy_start_month as string | undefined;
      // Use last month as reference date to calculate the financial year
      const now = new Date();
      const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const fyStart = getFinancialYearStart(lastMonthDate, fyStartMonth);
      const lastMonth = getLastMonth();
      dateRange = ` (${fyStart} - ${lastMonth})`;
      break;
    }
    case 'This Financial Year': {
      const fyStartMonth = fy_start_month as string | undefined;
      const now = new Date();
      const fyStart = getFinancialYearStart(now, fyStartMonth);
      const fyEnd = getFinancialYearEnd(now, fyStartMonth);
      dateRange = ` (${fyStart} - ${fyEnd})`;
      break;
    }
    default:
      break;
  }

  return `${preset}${dateRange}`;
};
